import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, CheckCheck, ChevronRight, ClipboardList } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useCompanyId } from '../../sale-order/hooks/useCompanyId';
import { isVesselNotice, type EmployeeNotification } from '../api/employeeNotificationsApi';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useMyNotifications } from '../hooks/useEmployeeNotifications';
import { useNoticeSocket } from '../hooks/useNoticeSocket';
import { showWhen } from './noticeTime';


/**
 * The header bell: the signed-in employee's notices (forwarding requests, mail and the like). Vessel
 * notices are NOT here: they have their own ship bell next to it (`VesselBell`), so they are never lost
 * among these (owner, 2026-10-13). The badge is the unread count of this bell only. The socket
 * (`useNoticeSocket`, one for both bells) reloads both and pops up the newest notice. Clicking a notice
 * marks it read and opens its link.
 */
export function NotificationBell() {
  const auth = useAuth();
  const companyId = useCompanyId(auth.companyId);
  const employeeId = Number(auth.employeeId) || 0;
  const navigate = useNavigate();
  const inbox = useMyNotifications(employeeId, 'other');
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead('other');
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const unread = inbox.data?.unreadCount ?? 0;
  const items = inbox.data?.items ?? [];

  const openNotice = (notice: EmployeeNotification) => {
    setOpen(false);
    if (!notice.read) markRead.mutate(notice.id);
    if (notice.linkPath) navigate(notice.linkPath);
  };

  useNoticeSocket(companyId, employeeId, openNotice);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (employeeId <= 0) return null;

  // a server without the group filter would still send vessel notices: never show them here
  const shown = items.filter((item) => !isVesselNotice(item.kind));

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 rounded-lg transition-all duration-200"
        style={{ color: unread > 0 ? 'var(--color-danger, #e11d48)' : 'var(--color-text-tertiary)' }}
        title={unread ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}
        aria-label="Notifications"
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-hover-overlay)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {unread > 0 ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ backgroundColor: 'var(--color-danger, #e11d48)' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Bell className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-black uppercase tracking-wide text-slate-600">Notifications</span>
            <span className="ml-auto text-[11px] text-slate-500">{unread ? `${unread} unread` : 'nothing unread'}</span>
            {unread > 0 && (
              <button type="button" title="Mark all read" onClick={() => markAll.mutate()}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <CheckCheck className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {shown.map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => openNotice(item)}
                  className={`flex w-full items-start gap-2 border-b border-slate-50 px-3 py-2 text-left transition hover:bg-blue-50 ${item.read ? 'opacity-60' : ''}`}>
                  <ClipboardList className={`mt-0.5 h-4 w-4 shrink-0 ${item.read ? 'text-slate-400' : 'text-amber-600'}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`truncate text-xs ${item.read ? 'font-semibold text-slate-600' : 'font-bold text-slate-800'}`}>{item.title}</span>
                      <span className="shrink-0 text-[10px] tabular-nums text-slate-400">{showWhen(item.createdDate)}</span>
                    </span>
                    {item.body && <span className="block truncate text-[11px] text-slate-600">{item.body}</span>}
                  </span>
                  {item.linkPath && <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-300" />}
                </button>
              </li>
            ))}
            {inbox.data && shown.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-slate-500">Nothing yet. Forwarding request updates appear here.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
